import type { FormEvent, ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { INVENTORY_API_URL, MATERIAL_TRANSFERS_API_URL, materialTransferPdfUrl, materialTransferReceiptUrl } from '../constants';
import { downloadBlob, parseContentDispositionFilename, readPdfExportErrorDetail } from '../utils/pdfDownload';
import { SourcePoRequestLinks } from '../components/sourceLinks/SourcePoRequestLinks';
import type { SourcePoRequestFields } from '../components/sourceLinks/SourcePoRequestLinks';
import type {
  LoginResponse,
  RegionDeliveryAddressDto,
  RegionFieldDto,
  RegionMasterDto,
  UserRole
} from '../domain/types';

type RegionFieldInfoForm = { id: string; name: string; county: string; state: string; closestCity: string };

type EndForm = {
  regionField: RegionFieldInfoForm;
  deliveryAddressId: string;
  deliveryAddress: string;
  afeNumber: string;
};

type LineForm = {
  materialLineKind: string;
  sku: string;
  inventoryItemId: string;
  /** Human-readable INV-… from catalog (not sent to API). */
  inventoryNumber: string;
  lineCondition: string;
  quantity: string;
  footage: string;
};

type InvLookup = {
  id: string;
  inventoryNumber: string;
  materialLineKind: string;
  sku: string;
  region: string;
  trackFootage: boolean;
  /** Catalog id; matches transfer From delivery when moving stock. */
  currentDeliveryAddressId?: string | null;
  currentDeliveryAddress?: string;
} & SourcePoRequestFields;

function normalizeAddressText(s: string | undefined | null): string {
  return (s ?? '').replace(/\s+/g, ' ').trim();
}

/** Stock must be at the same delivery as Transfer from (id match, or address text if id missing on lot). */
function inventoryMatchesFromDelivery(inv: InvLookup, from: EndForm): boolean {
  const fromId = from.deliveryAddressId?.trim() ?? '';
  if (!fromId) {
    return false;
  }
  const invId = (inv.currentDeliveryAddressId ?? '').trim();
  if (invId && fromId) {
    return invId.toLowerCase() === fromId.toLowerCase();
  }
  const a = normalizeAddressText(inv.currentDeliveryAddress);
  const b = normalizeAddressText(from.deliveryAddress);
  if (!a || !b) {
    return false;
  }
  return a.toLowerCase() === b.toLowerCase();
}

type TransferApi = {
  id: string;
  transferNumber: string;
  status: string;
  region: string;
  movementDateUtc: string;
  createdByUserId: string;
  createdByName: string;
  createdByRole: string;
  transferFrom: {
    regionField: { id: string; name: string; county: string; state: string; closestCity: string };
    deliveryAddressId: string | null;
    deliveryAddress: string;
    afeNumber: string;
  };
  transferTo: {
    regionField: { id: string; name: string; county: string; state: string; closestCity: string };
    deliveryAddressId: string | null;
    deliveryAddress: string;
    afeNumber: string;
  };
  lines: {
    materialLineKind: string;
    sku: string;
    inventoryItemId: string;
    lineCondition: string;
    quantity: number | null;
    footage: number | null;
  }[];
  fulfillmentProcurementRequestId?: string | null;
  fulfillmentRequestNumber?: string | null;
  receivedAtUtc?: string | null;
  receivedByName?: string | null;
  receiptFileName?: string | null;
  receiptBlobUrl?: string | null;
};

const emptyField = (): RegionFieldInfoForm => ({ id: '', name: '', county: '', state: '', closestCity: '' });

const emptyEnd = (): EndForm => ({
  regionField: emptyField(),
  deliveryAddressId: '',
  deliveryAddress: '',
  afeNumber: ''
});

const conditionOptions: { v: string; label: string }[] = [
  { v: 'new', label: 'New (move good stock)' },
  { v: 'bad', label: 'Bad' },
  { v: 'cut', label: 'Cut' },
  { v: 'badPin', label: 'Bad pin' }
];

function isProcurementStaffRole(role: UserRole): boolean {
  return (
    role === 'ProcurementManager' ||
    role === 'ProcurementSpecialist' ||
    role === 'Administrator' ||
    role === 'ITSupport'
  );
}

function formatApiErrorBody(raw: string, status: number, fallback: string): string {
  const text = raw.trim();
  if (!text) {
    return `${fallback} (HTTP ${status}).`;
  }
  try {
    const parsed: unknown = JSON.parse(text);
    if (typeof parsed === 'string' && parsed.trim()) {
      return parsed.trim();
    }
    if (parsed && typeof parsed === 'object') {
      const o = parsed as { message?: unknown; title?: unknown; detail?: unknown };
      const msg = [o.message, o.detail, o.title].find((v) => typeof v === 'string' && v.trim());
      if (typeof msg === 'string') {
        return msg.trim();
      }
    }
  } catch {
    /* not JSON */
  }
  if (/no such host|retry failed|blob\.core\.windows|azure\.requestfailedexception/i.test(text)) {
    return 'Could not reach file storage to save the movement ticket.';
  }
  if (text.startsWith('<') || /<!DOCTYPE/i.test(text)) {
    return `${fallback} (HTTP ${status}).`;
  }
  return text.length > 240 ? `${text.slice(0, 240)}…` : text;
}

function toIsoUtcFromLocalInput(localIso: string): string {
  if (!localIso) {
    return new Date().toISOString();
  }
  const d = new Date(localIso);
  if (Number.isNaN(d.getTime())) {
    return new Date().toISOString();
  }
  return d.toISOString();
}

function regionFieldToForm(f: RegionFieldDto): RegionFieldInfoForm {
  return {
    id: f.id ?? '',
    name: f.name ?? '',
    county: f.county ?? '',
    state: f.state ?? '',
    closestCity: f.closestCity ?? ''
  };
}

function mapApiToForm(t: TransferApi): {
  region: string;
  movementLocal: string;
  from: EndForm;
  to: EndForm;
  lines: LineForm[];
} {
  const md = t.movementDateUtc ? t.movementDateUtc.slice(0, 16) : '';
  return {
    region: t.region,
    movementLocal: md,
    from: {
      regionField: {
        id: t.transferFrom.regionField.id ?? '',
        name: t.transferFrom.regionField.name ?? '',
        county: t.transferFrom.regionField.county ?? '',
        state: t.transferFrom.regionField.state ?? '',
        closestCity: t.transferFrom.regionField.closestCity ?? ''
      },
      deliveryAddressId: t.transferFrom.deliveryAddressId ?? '',
      deliveryAddress: t.transferFrom.deliveryAddress ?? '',
      afeNumber: t.transferFrom.afeNumber ?? ''
    },
    to: {
      regionField: {
        id: t.transferTo.regionField.id ?? '',
        name: t.transferTo.regionField.name ?? '',
        county: t.transferTo.regionField.county ?? '',
        state: t.transferTo.regionField.state ?? '',
        closestCity: t.transferTo.regionField.closestCity ?? ''
      },
      deliveryAddressId: t.transferTo.deliveryAddressId ?? '',
      deliveryAddress: t.transferTo.deliveryAddress ?? '',
      afeNumber: t.transferTo.afeNumber ?? ''
    },
    lines: (t.lines ?? []).map((l) => ({
      materialLineKind: l.materialLineKind,
      sku: l.sku,
      inventoryItemId: l.inventoryItemId,
      inventoryNumber: '',
      lineCondition: (l.lineCondition as string) ?? 'new',
      quantity: l.quantity == null ? '' : String(l.quantity),
      footage: l.footage == null ? '' : String(l.footage)
    }))
  };
}

function MaterialTransferEndSection(props: {
  title: string;
  end: EndForm;
  onChange: (next: EndForm) => void;
  currentRegion: RegionMasterDto | undefined;
  quickHint?: ReactNode;
  readOnly?: boolean;
}): ReactElement {
  const { title, end, onChange, currentRegion, quickHint, readOnly = false } = props;

  const fieldById = useMemo(() => {
    const m = new Map<string, RegionFieldDto>();
    for (const f of currentRegion?.fields ?? []) {
      m.set(f.id, f);
    }
    return m;
  }, [currentRegion]);

  const deliveryById = useMemo(() => {
    const m = new Map<string, RegionDeliveryAddressDto>();
    for (const d of currentRegion?.deliveryAddresses ?? []) {
      m.set(d.id, d);
    }
    return m;
  }, [currentRegion]);

  const selectedField = end.regionField.id ? fieldById.get(end.regionField.id) : undefined;
  const fieldOrphan =
    end.regionField.id && !selectedField
      ? end.regionField
      : null;

  const selectedDelivery = end.deliveryAddressId
    ? deliveryById.get(end.deliveryAddressId)
    : undefined;
  const deliveryOrphan = end.deliveryAddressId && !selectedDelivery;

  return (
    <div className="mt-transfer-end-column">
      <h3 className="form-section-title">{title}</h3>
      {!currentRegion && (
        <p className="hint full-width">Select a region in the header to choose a field and delivery address.</p>
      )}
      {currentRegion && currentRegion.fields.length === 0 && (
        <p className="hint full-width">No region fields are configured for {currentRegion.name}.</p>
      )}
      {!readOnly && quickHint}
      {currentRegion && currentRegion.fields.length > 0 && (
        <label className="full-width">
          Region field
          <select
            value={end.regionField.id}
            onChange={(e) => {
              const id = e.target.value;
              const f = id ? fieldById.get(id) : undefined;
              onChange({
                ...end,
                regionField: f ? regionFieldToForm(f) : emptyField()
              });
            }}
            required
            disabled={readOnly}
          >
            <option value="">Select region field…</option>
            {currentRegion.fields.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name} — {f.county}, {f.state}
              </option>
            ))}
            {fieldOrphan && (
              <option value={fieldOrphan.id}>
                {fieldOrphan.name || 'Saved field'} (not in catalog)
              </option>
            )}
          </select>
        </label>
      )}
      {(end.regionField.id && (selectedField || fieldOrphan)) && (
        <div
          className="full-width"
          style={{
            marginTop: '0.25rem',
            marginBottom: '0.5rem',
            padding: '0.75rem',
            borderRadius: 6,
            background: 'var(--sage-surface-2, #f4f5f7)',
            border: '1px solid var(--sage-border, #d8dbe2)'
          }}
        >
          <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>Selected region field</p>
          <dl className="material-transfer-dl" style={{ margin: 0 }}>
            <div>
              <dt>Field id</dt>
              <dd className="mono-id">{end.regionField.id}</dd>
            </div>
            <div>
              <dt>Name</dt>
              <dd>{end.regionField.name || '—'}</dd>
            </div>
            <div>
              <dt>County</dt>
              <dd>{end.regionField.county || '—'}</dd>
            </div>
            <div>
              <dt>State</dt>
              <dd>{end.regionField.state || '—'}</dd>
            </div>
            <div>
              <dt>Closest city</dt>
              <dd>{end.regionField.closestCity || '—'}</dd>
            </div>
            {selectedField?.milesFromCity != null && (
              <div>
                <dt>Miles from city</dt>
                <dd>{String(selectedField.milesFromCity)}</dd>
              </div>
            )}
          </dl>
        </div>
      )}

      {currentRegion && (
        <label className="full-width">
          Delivery address
          <select
            value={end.deliveryAddressId}
            onChange={(e) => {
              const id = e.target.value;
              if (!id) {
                onChange({ ...end, deliveryAddressId: '', deliveryAddress: '' });
                return;
              }
              const d = deliveryById.get(id);
              onChange({
                ...end,
                deliveryAddressId: id,
                deliveryAddress: d?.address ?? end.deliveryAddress
              });
            }}
            disabled={readOnly}
          >
            <option value="">— None —</option>
            {currentRegion.deliveryAddresses.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
            {deliveryOrphan && (
              <option value={end.deliveryAddressId}>
                {end.deliveryAddressId.slice(0, 8)}… (not in catalog)
              </option>
            )}
          </select>
        </label>
      )}

      {end.deliveryAddressId && (selectedDelivery || deliveryOrphan) && (
        <div
          className="full-width"
          style={{
            marginTop: '0.25rem',
            marginBottom: '0.5rem',
            padding: '0.75rem',
            borderRadius: 6,
            background: 'var(--sage-surface-2, #f4f5f7)',
            border: '1px solid var(--sage-border, #d8dbe2)'
          }}
        >
          <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>Complete delivery address</p>
          {selectedDelivery && (
            <dl className="material-transfer-dl" style={{ margin: 0 }}>
              <div>
                <dt>Label</dt>
                <dd>{selectedDelivery.label}</dd>
              </div>
              <div>
                <dt>Address</dt>
                <dd style={{ whiteSpace: 'pre-wrap' }}>{selectedDelivery.address}</dd>
              </div>
              {selectedDelivery.category && (
                <div>
                  <dt>Category</dt>
                  <dd>{selectedDelivery.category}</dd>
                </div>
              )}
            </dl>
          )}
          {deliveryOrphan && !selectedDelivery && (
            <p className="hint" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {end.deliveryAddress || 'Saved address not found in the current region catalog.'}
            </p>
          )}
        </div>
      )}

      <label>
        AFE#
        <input
          value={end.afeNumber}
          onChange={(e) => onChange({ ...end, afeNumber: e.target.value })}
          readOnly={readOnly}
        />
      </label>
    </div>
  );
}

export function MaterialTransferFormPage(props: {
  auth: LoginResponse;
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
  activeRegions: RegionMasterDto[];
  mode: 'new' | 'edit';
}): ReactElement {
  const { auth, authFetch, activeRegions, mode } = props;
  const { transferId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNew = mode === 'new' || !transferId;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [lookup, setLookup] = useState<InvLookup[]>([]);
  const [lookupReady, setLookupReady] = useState(false);
  const [apiRow, setApiRow] = useState<TransferApi | null>(null);

  const [region, setRegion] = useState('');
  const [movementLocal, setMovementLocal] = useState(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  });
  const [from, setFrom] = useState<EndForm>(emptyEnd);
  const [to, setTo] = useState<EndForm>(emptyEnd);
  const [lines, setLines] = useState<LineForm[]>([]);
  const [prefillApplied, setPrefillApplied] = useState(false);
  const [prefillLineApplied, setPrefillLineApplied] = useState(false);
  const [prefillHint, setPrefillHint] = useState<string | null>(null);
  const [fulfillmentRequestId, setFulfillmentRequestId] = useState('');
  const [fulfillmentRequestNumber, setFulfillmentRequestNumber] = useState('');
  const [receivedLocal, setReceivedLocal] = useState(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  });
  const [receivedBy, setReceivedBy] = useState((auth.displayName ?? '').trim());
  const [receiptFile, setReceiptFile] = useState<File | null>(null);

  const regionOptions = useMemo(() => {
    const p = auth.profile;
    if (!p) {
      return activeRegions.filter((r) => r.isActive);
    }
    if (p.allRegions) {
      return activeRegions.filter((r) => r.isActive);
    }
    const codes = new Set(p.assignedRegionCodes.map((c) => c.toUpperCase()));
    return activeRegions.filter((r) => r.isActive && codes.has(r.code.toUpperCase()));
  }, [auth.profile, activeRegions]);

  const defaultRegion = useMemo(() => {
    if (regionOptions.length === 0) {
      return '';
    }
    return regionOptions[0].code;
  }, [regionOptions]);

  const currentRegion = useMemo(
    () => regionOptions.find((r) => r.code === region),
    [regionOptions, region]
  );

  const loadLookup = useCallback(async () => {
    try {
      const res = await authFetch(`${INVENTORY_API_URL}/lookup`);
      if (res.ok) {
        const list = (await res.json()) as InvLookup[];
        setLookup(list);
      }
    } finally {
      setLookupReady(true);
    }
  }, [authFetch]);

  useEffect(() => {
    void loadLookup();
  }, [loadLookup]);

  useEffect(() => {
    if (isNew) {
      return;
    }
    if (!transferId) {
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await authFetch(`${MATERIAL_TRANSFERS_API_URL}/${encodeURIComponent(transferId)}`);
        if (!res.ok) {
          if (!cancelled) {
            setErr('Transfer not found or access denied.');
          }
          return;
        }
        const t = (await res.json()) as TransferApi;
        if (cancelled) {
          return;
        }
        setApiRow(t);
        const m = mapApiToForm(t);
        setRegion(m.region);
        setMovementLocal(m.movementLocal);
        setFrom(m.from);
        setTo(m.to);
        setLines(m.lines);
        setFulfillmentRequestId((t.fulfillmentProcurementRequestId ?? '').trim());
        setFulfillmentRequestNumber((t.fulfillmentRequestNumber ?? '').trim());
        if (t.receivedAtUtc) {
          setReceivedLocal(t.receivedAtUtc.slice(0, 16));
        }
        if (t.receivedByName?.trim()) {
          setReceivedBy(t.receivedByName.trim());
        }
      } catch {
        if (!cancelled) {
          setErr('Failed to load transfer.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNew, transferId, authFetch]);

  useEffect(() => {
    if (isNew && !region && defaultRegion) {
      setRegion(defaultRegion);
    }
  }, [isNew, defaultRegion, region]);

  useEffect(() => {
    if (!isNew || prefillApplied) {
      return;
    }
    const regionCode = (searchParams.get('region') ?? '').trim();
    const sku = (searchParams.get('sku') ?? '').trim();
    const fromDeliveryId = (searchParams.get('fromDeliveryId') ?? '').trim();
    const toDeliveryId = (searchParams.get('toDeliveryId') ?? '').trim();
    const fromFieldId = (searchParams.get('fromFieldId') ?? '').trim();
    const toFieldId = (searchParams.get('toFieldId') ?? '').trim();
    const fromAfe = (searchParams.get('fromAfe') ?? '').trim();
    const toAfe = (searchParams.get('toAfe') ?? '').trim();
    const requestNumber = (searchParams.get('requestNumber') ?? '').trim();
    const requestId = (searchParams.get('requestId') ?? '').trim();
    if (requestId) {
      setFulfillmentRequestId(requestId);
    }
    if (requestNumber) {
      setFulfillmentRequestNumber(requestNumber);
    }
    if (!regionCode && !sku && !fromDeliveryId && !toDeliveryId) {
      setPrefillApplied(true);
      return;
    }
    if (regionCode && region !== regionCode) {
      setRegion(regionCode);
      return;
    }
    if (regionCode && !currentRegion) {
      if (regionOptions.length > 0 && !regionOptions.some((r) => r.code === regionCode)) {
        setPrefillHint(
          `Region ${regionCode} is not in your assigned regions. Select a region and complete From/To manually.`
        );
        setPrefillApplied(true);
      }
      return;
    }

    const applyEnd = (deliveryId: string, fieldId: string, afe: string): EndForm => {
      const d = currentRegion?.deliveryAddresses.find((x) => x.id === deliveryId);
      const f = currentRegion?.fields.find((x) => x.id === fieldId);
      return {
        regionField: f
          ? regionFieldToForm(f)
          : fieldId
            ? { ...emptyField(), id: fieldId }
            : emptyField(),
        deliveryAddressId: deliveryId,
        deliveryAddress: d?.address ?? '',
        afeNumber: afe
      };
    };

    if (fromDeliveryId || fromFieldId || fromAfe) {
      setFrom(applyEnd(fromDeliveryId, fromFieldId, fromAfe));
    }
    if (toDeliveryId || toFieldId || toAfe) {
      setTo(applyEnd(toDeliveryId, toFieldId, toAfe));
    }
    if (sku || requestNumber) {
      const bits = [
        requestNumber ? `Request ${requestNumber}` : null,
        sku ? `SKU ${sku}` : null
      ].filter(Boolean);
      setPrefillHint(
        `Prefilling a transfer${bits.length ? ` for ${bits.join(', ')}` : ''} toward this request’s delivery. Saving the draft moves the request to sourcing. Approve plans the transfer and moves a fully covered request to Procured. Attach the movement ticket to complete the transfer and the request. Cancel a planned transfer to return a fully covered request to procurement review.`
      );
    }
    setPrefillApplied(true);
  }, [isNew, prefillApplied, searchParams, region, currentRegion, regionOptions]);

  useEffect(() => {
    if (!isNew || !prefillApplied || prefillLineApplied || !lookupReady) {
      return;
    }
    const inventoryItemId = (searchParams.get('inventoryItemId') ?? '').trim();
    if (!inventoryItemId) {
      setPrefillLineApplied(true);
      return;
    }
    const inv = lookup.find((x) => x.id === inventoryItemId);
    if (!inv) {
      setPrefillLineApplied(true);
      return;
    }
    setLines((prev) => {
      if (prev.some((l) => l.inventoryItemId === inventoryItemId)) {
        return prev;
      }
      return [
        ...prev,
        {
          materialLineKind: inv.materialLineKind,
          sku: inv.sku,
          inventoryItemId: inv.id,
          inventoryNumber: (inv.inventoryNumber ?? '').trim(),
          lineCondition: 'new',
          quantity: (searchParams.get('quantity') ?? '').trim(),
          footage: (searchParams.get('footage') ?? '').trim()
        }
      ];
    });
    setPrefillLineApplied(true);
  }, [isNew, prefillApplied, prefillLineApplied, lookup, lookupReady, searchParams]);

  const myUserId = (auth.username ?? auth.profile?.userId ?? '').trim();
  const isOwner = isNew ? true : apiRow ? (apiRow.createdByUserId ?? '').trim() === myUserId : false;
  const status = (apiRow?.status ?? 'draft').toLowerCase();
  const canEdit =
    isNew ||
    (apiRow &&
      ((status === 'draft' &&
        isOwner &&
        (auth.role === 'Requestor' || isProcurementStaffRole(auth.role))) ||
        (['draft', 'submitted'].includes(status) && isProcurementStaffRole(auth.role))));
  const isDraft = status === 'draft';
  const isSubmitted = status === 'submitted';
  const isApproved = status === 'approved';
  const isPlanned = status === 'planned';
  const isCompleted = status === 'completed';
  const isCancelled = status === 'cancelled';

  const canSubmit = isOwner && isDraft && !isProcurementStaffRole(auth.role);
  const canApprove =
    isProcurementStaffRole(auth.role) && (isDraft || isSubmitted) && lines.length > 0 && canEdit;
  const canReject = isProcurementStaffRole(auth.role) && (isDraft || isSubmitted) && canEdit;
  const canCancel =
    !isNew &&
    !!transferId &&
    ((isDraft || isSubmitted) && (isProcurementStaffRole(auth.role) || (isOwner && isDraft)) ||
      (isPlanned && isProcurementStaffRole(auth.role)));
  const canComplete = !isNew && !!transferId && isPlanned && isProcurementStaffRole(auth.role);
  const readOnly = !isNew && !canEdit;

  const filteredLookup = useMemo(() => {
    const inRegion = lookup.filter((x) => !region || x.region === region);
    return inRegion.filter((x) => inventoryMatchesFromDelivery(x, from));
  }, [lookup, region, from]);

  /** After load, resolve INV numbers from the lookup list (ids only on API lines). */
  useEffect(() => {
    if (lookup.length === 0) {
      return;
    }
    setLines((prev) => {
      let changed = false;
      const next = prev.map((line) => {
        const num = (lookup.find((x) => x.id === line.inventoryItemId)?.inventoryNumber ?? '').trim();
        if (num && line.inventoryNumber !== num) {
          changed = true;
          return { ...line, inventoryNumber: num };
        }
        return line;
      });
      return changed ? next : prev;
    });
  }, [lookup]);

  function addLineFromInventory(invId: string): void {
    const inv = lookup.find((x) => x.id === invId);
    if (!inv) {
      return;
    }
    setLines((prev) => [
      ...prev,
      {
        materialLineKind: inv.materialLineKind,
        sku: inv.sku,
        inventoryItemId: inv.id,
        inventoryNumber: (inv.inventoryNumber ?? '').trim(),
        lineCondition: 'new',
        quantity: '',
        footage: ''
      }
    ]);
  }

  function updateLine(i: number, part: Partial<LineForm>): void {
    setLines((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], ...part };
      return next;
    });
  }

  function removeLine(i: number): void {
    setLines((prev) => prev.filter((_, j) => j !== i));
  }

  function buildPayload() {
    const toEnd = (e: EndForm) => ({
      regionField: {
        id: e.regionField.id.trim(),
        name: e.regionField.name.trim(),
        county: e.regionField.county.trim(),
        state: e.regionField.state.trim(),
        closestCity: e.regionField.closestCity.trim()
      },
      deliveryAddressId: e.deliveryAddressId.trim() || null,
      deliveryAddress: e.deliveryAddress.trim(),
      afeNumber: e.afeNumber.trim()
    });
    return {
      region: region.trim(),
      movementDateUtc: toIsoUtcFromLocalInput(movementLocal),
      transferFrom: toEnd(from),
      transferTo: toEnd(to),
      lines: lines.map((l) => ({
        materialLineKind: l.materialLineKind.trim(),
        sku: l.sku.trim(),
        inventoryItemId: l.inventoryItemId.trim(),
        lineCondition: l.lineCondition.trim() || 'new',
        quantity: l.quantity === '' ? null : Number(l.quantity),
        footage: l.footage === '' ? null : Number(l.footage)
      })),
      fulfillmentProcurementRequestId: fulfillmentRequestId.trim() || null,
      fulfillmentRequestNumber: fulfillmentRequestNumber.trim() || null
    };
  }

  async function saveDraft(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!region.trim()) {
      setErr('Region is required.');
      return;
    }
    setSaving(true);
    setErr(null);
    setMsg(null);
    const body = buildPayload();
    try {
      if (isNew) {
        const res = await authFetch(MATERIAL_TRANSFERS_API_URL, {
          method: 'POST',
          body: JSON.stringify(body)
        });
        if (!res.ok) {
          const t = await res.text();
          setErr(t || 'Could not create transfer.');
          return;
        }
        const created = (await res.json()) as TransferApi;
        setMsg('Transfer created.');
        navigate(`/material-transfers/${encodeURIComponent(created.id)}/edit`, { replace: true });
        return;
      }
      if (!transferId) {
        return;
      }
      const res = await authFetch(`${MATERIAL_TRANSFERS_API_URL}/${encodeURIComponent(transferId)}`, {
        method: 'PUT',
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const t = await res.text();
        setErr(t || 'Could not save transfer.');
        return;
      }
      setMsg('Saved.');
    } finally {
      setSaving(false);
    }
  }

  async function doSubmit(): Promise<void> {
    if (!transferId) {
      return;
    }
    if (!region.trim()) {
      setErr('Region is required.');
      return;
    }
    setSaving(true);
    setErr(null);
    setMsg(null);
    try {
      const put = await authFetch(
        `${MATERIAL_TRANSFERS_API_URL}/${encodeURIComponent(transferId)}`,
        { method: 'PUT', body: JSON.stringify(buildPayload()) }
      );
      if (!put.ok) {
        const t = await put.text();
        setErr(t || 'Could not save transfer before submit.');
        return;
      }
      const res = await authFetch(
        `${MATERIAL_TRANSFERS_API_URL}/${encodeURIComponent(transferId)}/submit`,
        { method: 'POST' }
      );
      if (!res.ok) {
        const t = await res.text();
        setErr(t || 'Submit failed.');
        return;
      }
      setMsg('Submitted for procurement review.');
      const t = (await res.json()) as TransferApi;
      setApiRow(t);
    } finally {
      setSaving(false);
    }
  }

  async function doApprove(): Promise<void> {
    if (!transferId) {
      return;
    }
    if (!region.trim()) {
      setErr('Region is required.');
      return;
    }
    setSaving(true);
    setErr(null);
    setMsg(null);
    try {
      // Approve uses server-side TransferFrom/TransferTo (incl. AFE#) — save current form first.
      const put = await authFetch(
        `${MATERIAL_TRANSFERS_API_URL}/${encodeURIComponent(transferId)}`,
        { method: 'PUT', body: JSON.stringify(buildPayload()) }
      );
      if (!put.ok) {
        const t = await put.text();
        setErr(t || 'Could not save transfer before approval.');
        return;
      }
      const res = await authFetch(
        `${MATERIAL_TRANSFERS_API_URL}/${encodeURIComponent(transferId)}/approve`,
        { method: 'POST', body: JSON.stringify({ comment: null }) }
      );
      if (!res.ok) {
        const t = await res.text();
        setErr(t || 'Approval failed.');
        return;
      }
      setMsg(
        fulfillmentRequestNumber
          ? `Transfer planned. If the yard covered the full remaining quantity, ${fulfillmentRequestNumber} is Procured. Attach the movement ticket to complete the transfer and the request. Cancel returns a fully covered request to procurement review.`
          : 'Transfer planned. Attach the movement ticket (received date, received by, and receipt) to move inventory and complete it.'
      );
      const t = (await res.json()) as TransferApi;
      setApiRow(t);
    } finally {
      setSaving(false);
    }
  }

  async function doReject(): Promise<void> {
    if (!transferId) {
      return;
    }
    const comment = window.prompt('Rejection reason (optional):', '') ?? undefined;
    setSaving(true);
    setErr(null);
    setMsg(null);
    try {
      const res = await authFetch(
        `${MATERIAL_TRANSFERS_API_URL}/${encodeURIComponent(transferId)}/reject`,
        { method: 'POST', body: JSON.stringify({ comment: comment || null }) }
      );
      if (!res.ok) {
        const t = await res.text();
        setErr(t || 'Reject failed.');
        return;
      }
      setMsg('Transfer rejected.');
      const t = (await res.json()) as TransferApi;
      setApiRow(t);
    } finally {
      setSaving(false);
    }
  }

  async function doCancel(): Promise<void> {
    if (!transferId) {
      return;
    }
    const ok = window.confirm(
      fulfillmentRequestNumber
        ? `Cancel this transfer? If ${fulfillmentRequestNumber} is Procured from this planned transfer, it will return to procurement review. A sourcing-only draft/submit cancel also returns it to review if nothing else is in flight.`
        : 'Cancel this transfer? A planned transfer can be cancelled before the movement ticket is attached.'
    );
    if (!ok) {
      return;
    }
    setSaving(true);
    setErr(null);
    setMsg(null);
    try {
      const res = await authFetch(
        `${MATERIAL_TRANSFERS_API_URL}/${encodeURIComponent(transferId)}/cancel`,
        { method: 'POST', body: JSON.stringify({ comment: null }) }
      );
      if (!res.ok) {
        const t = await res.text();
        setErr(t || 'Cancel failed.');
        return;
      }
      setMsg(
        fulfillmentRequestNumber
          ? `Transfer cancelled. ${fulfillmentRequestNumber} returned to procurement review if this planned transfer fully covered it, or if it was the only sourcing step.`
          : 'Transfer cancelled.'
      );
      const t = (await res.json()) as TransferApi;
      setApiRow(t);
    } finally {
      setSaving(false);
    }
  }

  async function doComplete(): Promise<void> {
    if (!transferId) {
      return;
    }
    if (!receivedBy.trim()) {
      setErr('Received by is required.');
      return;
    }
    if (!receivedLocal.trim()) {
      setErr('Received date is required.');
      return;
    }
    if (!receiptFile) {
      setErr('Attach the movement ticket (receipt) to complete this transfer.');
      return;
    }
    setSaving(true);
    setErr(null);
    setMsg(null);
    try {
      const body = new FormData();
      body.append('receivedBy', receivedBy.trim());
      body.append('receivedDate', toIsoUtcFromLocalInput(receivedLocal));
      body.append('file', receiptFile);
      const res = await authFetch(
        `${MATERIAL_TRANSFERS_API_URL}/${encodeURIComponent(transferId)}/complete`,
        { method: 'POST', body }
      );
      if (!res.ok) {
        const t = await res.text();
        setErr(formatApiErrorBody(t, res.status, 'Could not complete transfer'));
        return;
      }
      setMsg(
        fulfillmentRequestNumber
          ? `Movement ticket recorded. Inventory was moved. If this transfer fully covered ${fulfillmentRequestNumber}, that request is Complete.`
          : 'Movement ticket recorded. Inventory was moved and the transfer is complete.'
      );
      const t = (await res.json()) as TransferApi;
      setApiRow(t);
      setReceiptFile(null);
    } catch {
      setErr('Could not complete transfer. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  }

  async function downloadReceipt(): Promise<void> {
    if (!transferId) {
      return;
    }
    const res = await authFetch(materialTransferReceiptUrl(transferId));
    if (!res.ok) {
      setErr((await res.text()) || 'Could not download the movement ticket.');
      return;
    }
    const blob = await res.blob();
    const name =
      parseContentDispositionFilename(res.headers.get('Content-Disposition')) ||
      apiRow?.receiptFileName ||
      'movement-ticket';
    downloadBlob(blob, name);
  }

  const exportMaterialTransferPdf = useCallback(async (): Promise<void> => {
    if (!transferId) {
      return;
    }
    setPdfExporting(true);
    setErr(null);
    try {
      const response = await authFetch(materialTransferPdfUrl(transferId));
      if (!response.ok) {
        const detail = await readPdfExportErrorDetail(response);
        setErr(
          detail
            ? `Could not export PDF (HTTP ${response.status}). ${detail}`
            : `Could not export PDF (HTTP ${response.status}).`
        );
        return;
      }
      const blob = await response.blob();
      const safeNum = (apiRow?.transferNumber ?? 'material-transfer').replace(/[^a-zA-Z0-9._-]+/g, '-');
      const name =
        parseContentDispositionFilename(response.headers.get('Content-Disposition')) ?? `${safeNum}.pdf`;
      downloadBlob(blob, name);
    } catch {
      setErr('Could not export PDF.');
    } finally {
      setPdfExporting(false);
    }
  }, [transferId, authFetch, apiRow?.transferNumber]);

  function fieldPicker(r: RegionMasterDto, which: 'from' | 'to'): void {
    if (r.fields.length === 0) {
      return;
    }
    const rf = regionFieldToForm(r.fields[0]);
    if (which === 'from') {
      setFrom((f) => ({ ...f, regionField: rf }));
    } else {
      setTo((f) => ({ ...f, regionField: rf }));
    }
  }

  return (
    <div className="card">
      <div className="row spread" style={{ alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>
          {isNew ? 'New Material Transfer' : `Edit Material Transfer ${apiRow?.transferNumber ?? ''}`}
        </h2>
        <div className="row" style={{ gap: '0.5rem' }}>
          {!isNew && transferId && (
            <button
              type="button"
              className="secondary panel-export-btn"
              disabled={pdfExporting || loading}
              onClick={() => void exportMaterialTransferPdf()}
            >
              {pdfExporting ? 'Exporting…' : 'Export to PDF'}
            </button>
          )}
          <button type="button" className="secondary" onClick={() => navigate('/material-transfers')}>
            Back to list
          </button>
        </div>
      </div>
      {readOnly && !isNew ? (
        <p className="hint" role="status">
          {isPlanned
            ? 'This transfer is planned. Inventory has not moved yet. Attach a movement ticket to complete it, or cancel to withdraw the plan.'
            : isCompleted
              ? 'This transfer is complete. The movement ticket is on file and inventory has been updated.'
              : isApproved
                ? 'This transfer was approved under the previous process (inventory already moved). Details below are read-only.'
                : isCancelled
                  ? 'This transfer was cancelled. Details below are read-only.'
                  : 'This transfer cannot be edited in its current state. Details below are read-only.'}
        </p>
      ) : (
        <p className="hint">
          {isProcurementStaffRole(auth.role)
            ? 'Procurement: save a draft, add lines, then Approve to plan the transfer. Attach a movement ticket later to move inventory and complete it.'
            : 'Save your draft, add lines, then submit for a procurement manager to review, plan, or reject.'}
        </p>
      )}
      {prefillHint && (
        <p className="request-transfer-prefill-hint" role="status">
          {prefillHint}
        </p>
      )}
      {!prefillHint && fulfillmentRequestNumber && (
        <p className="request-transfer-prefill-hint" role="status">
          This transfer fulfills request {fulfillmentRequestNumber}. Creating the draft moves that request to sourcing.
          Approve plans the transfer and moves the request to Procured when the yard covers the full remaining amount.
          Attach the movement ticket to complete the transfer and the request. Cancel a planned transfer to return a
          fully covered request to procurement review.
        </p>
      )}
      {err && <p className="danger-text">{err}</p>}
      {msg && <p className="message">{msg}</p>}

      {loading && <p>Loading…</p>}

      {(isNew || (transferId && !loading)) && (
        <form
          onSubmit={
            readOnly
              ? (e) => {
                  e.preventDefault();
                }
              : saveDraft
          }
          className="grid admin-form material-transfer-form"
          style={{ marginTop: '0.5rem' }}
        >
          <h3>Header</h3>
          {apiRow && !isNew && (
            <p className="hint full-width" style={{ margin: 0 }}>
              Status:{' '}
              <strong>{(apiRow.status ?? '').replace(/^./, (c) => c.toUpperCase())}</strong>
            </p>
          )}
          <label>
            Region (scope)
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              required
              disabled={!isNew && readOnly}
            >
              <option value="">Select region</option>
              {regionOptions.map((r) => (
                <option key={r.id} value={r.code}>
                  {r.name} ({r.code})
                </option>
              ))}
            </select>
          </label>
          <label>
            Movement date (local, stored as UTC)
            <input
              type="datetime-local"
              value={movementLocal}
              onChange={(e) => setMovementLocal(e.target.value)}
              required
              readOnly={readOnly}
            />
          </label>

          <div className="full-width mt-transfer-ends" role="group" aria-label="Transfer from and to">
            <MaterialTransferEndSection
              title="Transfer from"
              end={from}
              onChange={setFrom}
              currentRegion={currentRegion}
              readOnly={readOnly}
              quickHint={
                currentRegion && currentRegion.fields.length > 0 ? (
                  <p className="hint full-width">
                    Quick fill:{' '}
                    <button type="button" className="secondary" onClick={() => fieldPicker(currentRegion, 'from')}>
                      Use first field in {currentRegion.code}
                    </button>
                  </p>
                ) : null
              }
            />
            <MaterialTransferEndSection
              title="Transfer to"
              end={to}
              onChange={setTo}
              currentRegion={currentRegion}
              readOnly={readOnly}
              quickHint={
                currentRegion && currentRegion.fields.length > 0 ? (
                  <p className="hint full-width">
                    <button type="button" className="secondary" onClick={() => fieldPicker(currentRegion, 'to')}>
                      Copy first field from selected region
                    </button>
                  </p>
                ) : null
              }
            />
          </div>

          <h3>Lines</h3>
          {!readOnly && (
            <>
              <p className="hint full-width">
                Add lines from on-hand lots whose <strong>current delivery address</strong> matches{' '}
                <strong>Transfer from → Delivery address</strong> (same region). Refine condition, quantity, and footage on
                each line.
              </p>
              {!from.deliveryAddressId?.trim() && (
                <p className="hint full-width">
                  Select a <strong>From</strong> delivery address above to see inventory you can move from that location.
                </p>
              )}
              {from.deliveryAddressId?.trim() && filteredLookup.length === 0 && (
                <p className="hint full-width">No on-hand items at this From delivery in the selected region.</p>
              )}
            </>
          )}
          {readOnly && (
            <p className="hint full-width">Lines on this transfer (read-only).</p>
          )}
          {!readOnly && (
            <div className="full-width" style={{ marginBottom: '0.5rem' }}>
              <label>
                Add from inventory
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      addLineFromInventory(e.target.value);
                      e.target.value = '';
                    }
                  }}
                  disabled={!region || !from.deliveryAddressId?.trim() || readOnly}
                >
                <option value="">
                  {!from.deliveryAddressId?.trim() ? 'Select a From delivery address first…' : 'Select inventory item…'}
                </option>
                {filteredLookup.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.inventoryNumber} — {i.sku} ({i.materialLineKind})
                  </option>
                ))}
                </select>
              </label>
            </div>
          )}

          {lines.map((line, idx) => {
            const invRow = lookup.find((x) => x.id === line.inventoryItemId);
            return (
            <div
              key={`${line.inventoryItemId}-${idx}`}
              className="full-width"
              style={{ border: '1px solid var(--sage-border, #d8dbe2)', borderRadius: 6, padding: '0.75rem' }}
            >
              <div className="row spread">
                <strong>
                  {line.sku} / {line.materialLineKind}
                </strong>
                {!readOnly && (
                  <button type="button" className="secondary danger-text" onClick={() => removeLine(idx)}>
                    Remove
                  </button>
                )}
              </div>
              <p className="mono-id" style={{ fontSize: '0.85rem' }}>
                Inventory number: {line.inventoryNumber?.trim() || '—'}
              </p>
              {invRow && (
                <SourcePoRequestLinks
                  sourcePurchaseOrderId={invRow.sourcePurchaseOrderId}
                  sourcePurchaseOrderNumber={invRow.sourcePurchaseOrderNumber}
                  sourceProcurementRequestId={invRow.sourceProcurementRequestId}
                  sourceRequestNumber={invRow.sourceRequestNumber}
                />
              )}
              <div className="mt-line-field-row">
                <label>
                  Condition
                  <select
                    value={line.lineCondition}
                    onChange={(e) => updateLine(idx, { lineCondition: e.target.value })}
                    disabled={readOnly}
                  >
                    {conditionOptions.map((c) => (
                      <option key={c.v} value={c.v}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Quantity (optional)
                  <input
                    value={line.quantity}
                    onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                    placeholder={fulfillmentRequestId ? 'Capped to remaining request qty' : 'Leave blank for full on-hand'}
                    readOnly={readOnly}
                  />
                </label>
                <label>
                  Footage (optional)
                  <input
                    value={line.footage}
                    onChange={(e) => updateLine(idx, { footage: e.target.value })}
                    placeholder={fulfillmentRequestId ? 'Capped to remaining request footage' : 'Length material'}
                    readOnly={readOnly}
                  />
                </label>
              </div>
            </div>
            );
          })}

          {!readOnly && (
            <div className="row" style={{ marginTop: '1rem' }}>
              <button type="submit" disabled={saving}>
                {saving ? 'Saving…' : isNew ? 'Create draft' : 'Save draft'}
              </button>
            </div>
          )}
        </form>
      )}

      {!isNew && transferId && !loading && (
        <div className="row" style={{ marginTop: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          {canSubmit && (
            <button type="button" onClick={() => void doSubmit()} disabled={saving} className="secondary">
              Submit for procurement review
            </button>
          )}
          {canApprove && (
            <button type="button" onClick={() => void doApprove()} disabled={saving}>
              Approve (plan transfer)
            </button>
          )}
          {canReject && (
            <button type="button" className="secondary danger-text" onClick={() => void doReject()} disabled={saving}>
              Reject
            </button>
          )}
          {canCancel && (
            <button type="button" className="secondary danger-text" onClick={() => void doCancel()} disabled={saving}>
              Cancel transfer
            </button>
          )}
        </div>
      )}

      {!isNew && transferId && !loading && (isPlanned || isCompleted) && (
        <section className="card" style={{ marginTop: '1.25rem' }} aria-label="Movement ticket">
          <h3>Movement ticket</h3>
          {isPlanned && (
            <p className="hint">
              Record received date, received by, and attach the actual receipt. That completes the transfer and moves
              inventory.
              {fulfillmentRequestNumber
                ? ` If this transfer fully covers ${fulfillmentRequestNumber}, that request becomes Complete.`
                : ''}
            </p>
          )}
          {isCompleted && (
            <p className="hint">
              Receipt recorded
              {apiRow?.receivedByName ? ` by ${apiRow.receivedByName}` : ''}
              {apiRow?.receivedAtUtc
                ? ` on ${apiRow.receivedAtUtc.replace('T', ' ').slice(0, 16)} UTC`
                : ''}
              {apiRow?.receiptFileName ? ` (${apiRow.receiptFileName})` : ''}.
            </p>
          )}
          {isPlanned && (
            <div className="grid admin-form" style={{ marginTop: '0.5rem' }}>
              <label>
                Received date
                <input
                  type="datetime-local"
                  value={receivedLocal}
                  onChange={(e) => setReceivedLocal(e.target.value)}
                  required
                />
              </label>
              <label>
                Received by
                <input
                  value={receivedBy}
                  onChange={(e) => setReceivedBy(e.target.value)}
                  required
                />
              </label>
              <label className="full-width">
                Receipt attachment (PDF or image)
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
                  onChange={(e) => setReceiptFile(e.target.files?.[0] ?? null)}
                />
              </label>
              {receiptFile && (
                <p className="hint full-width" style={{ margin: 0 }}>
                  Selected: {receiptFile.name}
                </p>
              )}
              {err && <p className="danger-text full-width">{err}</p>}
              <div className="row">
                <button type="button" onClick={() => void doComplete()} disabled={saving}>
                  {saving ? 'Saving…' : 'Attach ticket and complete'}
                </button>
              </div>
            </div>
          )}
          {isCompleted && apiRow?.receiptFileName && apiRow.receiptBlobUrl && (
            <div className="row" style={{ marginTop: '0.5rem' }}>
              <button type="button" className="secondary" onClick={() => void downloadReceipt()}>
                Download receipt
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
